// One conversation-open request on servers that support the bootstrap
// endpoint. On 404 the hook reports `unsupported` and the existing
// per-endpoint hooks keep working unchanged (independent UI/server deploys).
// Only route absence falls back — auth/authz/server failures surface as
// errors instead of triggering a duplicate request waterfall.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchConversationBootstrap,
  type BootstrapResult,
  type ConversationBootstrap,
} from "@/api/conversation-bootstrap";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import type { AccessTokenGetter } from "@/lib/utils";
import { cacheScope } from "@/lib/ui-cache";

export type BootstrapState =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "ready"; sessionId: string; data: ConversationBootstrap }
  | { phase: "unsupported" }
  | { phase: "error"; status: number };

// 404 answers are remembered per backend generation so an old server never
// sees a bootstrap attempt on every render/remount. A reconnect (or a bridge
// backend change) re-arms the probe.
const unsupportedGenerations = new Set<string>();
const generationKey = (generation: number) => `${cacheScope()}:${generation}`;

export function useConversationBootstrap(
  sessionId: string | null,
  getToken: AccessTokenGetter,
  enabled: boolean,
): { state: BootstrapState; refresh: () => void } {
  const backendReconnectKey = useBackendReconnectKey();
  const [state, setState] = useState<BootstrapState>({ phase: "idle" });
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const run = useCallback((
    currentSessionId: string,
    generation: number,
    requestId: number,
  ) => {
    const controller = new AbortController();
    void (async () => {
      let result: BootstrapResult;
      try {
        result = await fetchConversationBootstrap(currentSessionId, getToken, controller.signal);
      } catch (error) {
        // Aborted by unmount or session switch: never commit, and don't mark
        // the endpoint unsupported — the abort is ours, not the server's.
        if (controller.signal.aborted || !mountedRef.current) return;
        console.warn("[ui] conversation bootstrap failed:", error);
        if (requestRef.current === requestId) setState({ phase: "error", status: 0 });
        return;
      }
      if (!mountedRef.current || requestRef.current !== requestId) return;
      if (result.unsupported) {
        unsupportedGenerations.add(generationKey(generation));
        setState({ phase: "unsupported" });
        return;
      }
      if (result.data) {
        if (result.data.session.id !== currentSessionId) {
          setState({ phase: "error", status: result.status });
          return;
        }
        setState({ phase: "ready", sessionId: currentSessionId, data: result.data });
        return;
      }
      setState({ phase: "error", status: result.status });
    })();
    return () => controller.abort();
  }, [getToken]);

  useEffect(() => {
    const requestId = ++requestRef.current;
    if (!sessionId || !enabled) {
      setState({ phase: "idle" });
      return () => {};
    }
    if (unsupportedGenerations.has(generationKey(backendReconnectKey))) {
      setState({ phase: "unsupported" });
      return () => {};
    }
    setState({ phase: "loading" });
    return run(sessionId, backendReconnectKey, requestId);
  }, [sessionId, enabled, backendReconnectKey, run]);

  const refresh = useCallback(() => {
    if (!sessionId || !enabled || unsupportedGenerations.has(generationKey(backendReconnectKey))) return;
    const requestId = ++requestRef.current;
    setState({ phase: "loading" });
    run(sessionId, backendReconnectKey, requestId);
  }, [sessionId, enabled, backendReconnectKey, run]);

  return { state, refresh };
}
